import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Button, IconButton, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { today } from '../../../../../Js/Date';
import { viewAllBranchesAPICall } from '../../../../../API/Settings/Company Settings/companySettingsAPI';
import { vieWareHouseLocationAPICall } from '../../Configuration/wareHouseLocationAPI';
import { getAllNewProductList } from '../../../Accounts/Vendor/API/vendorBillAPI';
import { branchSettingsSingleView } from '../../../PointOfSale/Configuration/BranchSettings/BranchSettingsAPI';
import { savePhysicalClosingAPI, getPhysicalClosingAPI, getProductsFromStockMovesAPI } from './PhysicalClosingAPI';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

export const PhysicalClosingEntry = () => {
  // Redux selectors
  const allBranchesList = useSelector((state) => state.allBranchesSlice.value);
  const wareHouseLocationList = useSelector((state) => state.wareHouseLocationSlice.value);
  const allNewProductList = useSelector((state) => state.vendorBillSlice.NewProductList);
  const loginResponse = useSelector((state) => state.loginResponseSlice.value);
  const userRole = useSelector((state) => state.userRoleSlice.value);
  const branchSettingsView = useSelector((state) => state.branchSettingsSliceNew.rowData);
  
  // State
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [physicalClosingData, setPhysicalClosingData] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productsFromStockMoves, setProductsFromStockMoves] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [itemTypeFilter, setItemTypeFilter] = useState(['Stock', 'Service']); // Multi-select filter
  const [branchSettingsLoaded, setBranchSettingsLoaded] = useState(false);
  
  // Check if manual mode is enabled
  const isManualMode = branchSettingsView?.iaPhysicalClosingManual === true;
  
  // Debug: Log when manual mode status changes
  useEffect(() => {
    console.log('🔍 Physical Closing Manual Mode:', {
      isManualMode,
      branchSettingsView: branchSettingsView?.iaPhysicalClosingManual,
      branchId: loginResponse?.branchPk
    });
  }, [isManualMode, branchSettingsView]);
  
  useEffect(() => {
    // Fetch all required data
    viewAllBranchesAPICall();
    vieWareHouseLocationAPICall();
    getAllNewProductList();
    
    // Set default branch from login when available and valid
    if (loginResponse?.branchPk && loginResponse.branchPk !== 'noBranch' && allBranchesList && allBranchesList.length > 0) {
      const defaultBranch = allBranchesList.find(b => b._id === loginResponse.branchPk);
      if (defaultBranch) {
        setSelectedBranch(defaultBranch);
      }
    }
  }, []);

  // When selected branch changes, fetch latest branch settings (guard invalid ids)
  useEffect(() => {
    const effectiveBranchId = selectedBranch?._id || (loginResponse?.branchPk && loginResponse.branchPk !== 'noBranch' ? loginResponse.branchPk : null);
    if (!effectiveBranchId) {
      console.warn('⚠️ Skipping branch settings fetch: no valid branchId');
      return;
    }
    console.log('🔄 Fetching branch settings for branchId:', effectiveBranchId);
    branchSettingsSingleView({ branchId: effectiveBranchId });
  }, [selectedBranch]);

  // Mark settings as loaded when branchSettingsView changes
  useEffect(() => {
    if (branchSettingsView !== undefined) {
      setBranchSettingsLoaded(true);
    }
  }, [branchSettingsView]);

  // When branches list arrives, set a sensible default branch
  useEffect(() => {
    if (!selectedBranch && allBranchesList && allBranchesList.length > 0) {
      const loginBranchId = loginResponse?.branchPk;
      const byLogin = allBranchesList.find(b => b._id === loginBranchId);
      setSelectedBranch(byLogin || allBranchesList[0]);
    }
  }, [allBranchesList, loginResponse, selectedBranch]);
  
  // Normalize item type from various schemas (itemType/prodType/productType)
  const getItemType = (p) => {
    const raw = (p?.itemType || p?.prodType || p?.productType || '').toString();
    if (/^stock$/i.test(raw)) return 'Stock';
    if (/^service$/i.test(raw)) return 'Service';
    return raw || 'Stock';
  };

  // Filter products based on search term AND item type filter
  useEffect(() => {
    // Use products from stock moves (priority) or all products as fallback
    const sourceProducts = productsFromStockMoves.length > 0 ? productsFromStockMoves : [];
    
    if (sourceProducts.length > 0 && searchTerm) {
      const searchLower = String(searchTerm || '').toLowerCase();
      const filtered = sourceProducts.filter(product => {
        // Item type filter (Stock, Service, or both)
        const type = getItemType(product);
        const matchesType = itemTypeFilter.includes(type);
        if (!matchesType) return false;
        
        // Safe string coercion for search fields
        const name = String(product.itemName || product.name || '').toLowerCase();
        const code = String(product.itemCode || product.prodId || product.itemId || '').toLowerCase();
        
        return name.includes(searchLower) || code.includes(searchLower);
      });
      setFilteredProducts(filtered);
    } else if (sourceProducts.length > 0 && !searchTerm) {
      // Show all products from stock moves if no search term
      const filtered = sourceProducts.filter(product => {
        const type = getItemType(product);
        return itemTypeFilter.includes(type);
      });
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [searchTerm, itemTypeFilter, productsFromStockMoves]);
  
  // Load products from stock moves when date/branch/location change
  useEffect(() => {
    const loadProducts = async () => {
      if (!selectedBranch || !selectedLocation || !selectedDate) {
        setProductsFromStockMoves([]);
        setFilteredProducts([]);
        return;
      }
      
      setIsLoadingProducts(true);
      // Date range: selected date TO current date
      const fromDate = new Date(selectedDate).setHours(0, 0, 0, 0);
      const toDate = new Date().setHours(23, 59, 59, 999); // Current date
      
      const result = await getProductsFromStockMovesAPI({
        branchId: selectedBranch._id,
        locationId: selectedLocation._id,
        fromDate,
        toDate
      });
      
      if (result?.success && result?.data) {
        setProductsFromStockMoves(result.data);
        setFilteredProducts(result.data);
        console.log(`✅ Loaded ${result.data.length} products from stock moves`);
      } else {
        setProductsFromStockMoves([]);
        setFilteredProducts([]);
      }
      setIsLoadingProducts(false);
    };
    
    loadProducts();
  }, [selectedDate, selectedBranch, selectedLocation]);

  // Load existing physical closing data
  const loadPhysicalClosingData = async () => {
    if (!selectedBranch || !selectedLocation || !selectedDate) {
      alert('Please select Branch, Location, and Date');
      return;
    }
    
    setIsLoading(true);
    const result = await getPhysicalClosingAPI({
      branchId: selectedBranch._id,
      locationId: selectedLocation._id,
      date: new Date(selectedDate).getTime()
    });
    
    console.log('📖 Physical Closing API Result:', result);
    
    if (result?.success && result?.data) {
      // Backend returns { success: true, data: { success: true, data: [...] } }
      // So we need to access result.data.data
      let dataArray = [];
      if (Array.isArray(result.data)) {
        dataArray = result.data;
      } else if (result.data.data && Array.isArray(result.data.data)) {
        dataArray = result.data.data;
      }
      
      console.log('📖 Extracted data array:', dataArray);
      setPhysicalClosingData(dataArray);
      setSnackbar({ open: true, message: `Loaded ${dataArray.length} entries`, type: 'success' });
    } else {
      setPhysicalClosingData([]);
      setSnackbar({ open: true, message: 'No existing data found', type: 'info' });
    }
    setIsLoading(false);
  };
  
  // Update quantity for a product
  const updateQuantity = (productId, quantity) => {
    if (!Array.isArray(physicalClosingData)) {
      console.error('physicalClosingData is not an array:', physicalClosingData);
      setPhysicalClosingData([]);
      return;
    }
    const existingIndex = physicalClosingData.findIndex(item => item.itemId === productId);
    
    if (existingIndex >= 0) {
      const updated = [...physicalClosingData];
      updated[existingIndex].physicalQty = Number(quantity);
      setPhysicalClosingData(updated);
    } else {
      setPhysicalClosingData([
        ...physicalClosingData,
        {
          itemId: productId,
          physicalQty: Number(quantity),
          enteredBy: loginResponse?.userId || loginResponse?.email,
          enteredDate: Date.now()
        }
      ]);
    }
  };
  
  // Get quantity for a product
  const getQuantity = (productId) => {
    if (!Array.isArray(physicalClosingData)) {
      console.error('physicalClosingData is not an array:', physicalClosingData);
      return '';
    }
    const item = physicalClosingData.find(item => item.itemId === productId);
    return item?.physicalQty || '';
  };
  
  // Save physical closing data
  const savePhysicalClosing = async () => {
    if (!selectedBranch || !selectedLocation || !selectedDate) {
      alert('Please select Branch, Location, and Date');
      return;
    }
    
    if (physicalClosingData.length === 0) {
      alert('Please enter at least one physical closing quantity');
      return;
    }
    
    setIsSaving(true);
    const result = await savePhysicalClosingAPI({
      branchId: selectedBranch._id,
      locationId: selectedLocation._id,
      date: new Date(selectedDate).getTime(),
      entries: physicalClosingData
    });
    
    if (result?.success) {
      setSnackbar({ open: true, message: 'Physical closing saved successfully!', type: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Failed to save physical closing', type: 'error' });
    }
    setIsSaving(false);
  };
  
  // Clear all entries
  const clearEntries = () => {
    if (window.confirm('Are you sure you want to clear all entries?')) {
      setPhysicalClosingData([]);
    }
  };
  
  if (branchSettingsLoaded && !isManualMode) {
    return (
      <div className="global-page-parent-container">
        <p className="breadcrumb">Inventory &gt; Operations &gt; Physical Closing Entry</p>
        <div className="global-white-bg-container" style={{ padding: '40px', textAlign: 'center' }}>
          <h3>Manual Physical Closing Entry is Disabled</h3>
          <p style={{ marginTop: '20px', color: '#666' }}>
            To enable manual physical closing entry, go to:<br/>
            <strong>Point of Sale &gt; Configuration &gt; Branch Settings</strong><br/>
            and enable the <strong>"IA Physical Closing Manual"</strong> checkbox.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="global-page-parent-container">
      <p className="breadcrumb">Inventory &gt; Operations &gt; Physical Closing Entry</p>
      
      <div className="daily-report-top-container">
        <h3>Physical Closing Entry</h3>
        <hr className="global-hr" />
        
        {/* Filters */}
        <div className="daily-report-top-input-container">
          <div className="global-single-input auto-complete">
            <p>Date</p>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              fullWidth
            />
          </div>
          
          <div className="global-single-input auto-complete">
            <p>Branch</p>
            <Autocomplete
              sx={{ width: "100%" }}
              options={allBranchesList || []}
              getOptionLabel={(option) => `${option?.storeCode}-${option?.branchName}`}
              renderInput={(params) => <TextField {...params} placeholder="Select Branch" />}
              disabled={userRole === "user"}
              value={selectedBranch || null}
              onChange={(e, newValue) => setSelectedBranch(newValue)}
              isOptionEqualToValue={(option, value) => option._id === value._id}
            />
          </div>
          
          <div className="global-single-input auto-complete">
            <p>Location</p>
            <Autocomplete
              sx={{ width: "100%" }}
              options={wareHouseLocationList || []}
              getOptionLabel={(option) => option?.name}
              renderInput={(params) => <TextField {...params} placeholder="Select Location" />}
              value={selectedLocation || null}
              onChange={(e, newValue) => setSelectedLocation(newValue)}
              isOptionEqualToValue={(option, value) => option._id === value._id}
            />
          </div>
          
          {/* Load Existing Data button removed - data loads automatically */}
        </div>
      </div>
      
      {/* Product Search and Filters */}
      <div className="global-white-bg-container" style={{ marginTop: '20px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 280px', 
          gap: '20px', 
          marginBottom: '20px',
          alignItems: 'start'
        }}>
          <div>
            <p style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Search Products</p>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by product name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: '8px', color: '#999', fontSize: '20px' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            />
          </div>
          
          <div>
            <p style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Item Type</p>
            <Autocomplete
              multiple
              size="small"
              options={['Stock', 'Service']}
              value={itemTypeFilter}
              onChange={(e, newValue) => setItemTypeFilter(newValue.length > 0 ? newValue : ['Stock', 'Service'])}
              renderInput={(params) => <TextField {...params} placeholder="Select types" />}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            />
          </div>
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={savePhysicalClosing}
            disabled={isSaving || !Array.isArray(physicalClosingData) || physicalClosingData.length === 0}
            startIcon={<SaveIcon />}
          >
            {isSaving ? 'Saving...' : 'Save Physical Closing'}
          </Button>
          
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={clearEntries}
            disabled={!Array.isArray(physicalClosingData) || physicalClosingData.length === 0}
          >
            Clear All
          </Button>
          
          <div style={{ marginLeft: 'auto', color: '#666' }}>
            Entries: <strong>{Array.isArray(physicalClosingData) ? physicalClosingData.length : 0}</strong>
          </div>
        </div>
        
        {/* Products Table */}
        <div className="global-table-container" style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <table className="global-table">
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
              <tr>
                <th style={{ width: '50px' }}>No</th>
                <th style={{ width: '120px' }}>Item Code</th>
                <th>Item Name</th>
                <th style={{ width: '80px' }}>Type</th>
                <th style={{ width: '80px' }}>UOM</th>
                <th style={{ width: '120px' }}>Existing Stock</th>
                <th style={{ width: '120px' }}>Physical Qty</th>
                <th style={{ width: '120px' }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingProducts ? (
                [...Array(10)].map((r, i) => (
                  <tr key={i}>
                    <td colSpan={8}>
                      <Skeleton variant="rectangular" animation="wave" width={"100%"} height={30} />
                    </td>
                  </tr>
                ))
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map((product, index) => {
                  const physicalQty = getQuantity(product._id);
                  const existingStock = product.existingStock || 0;
                  const variance = physicalQty ? (Number(physicalQty) - existingStock).toFixed(2) : '';
                  
                  return (
                    <tr key={product._id}>
                      <td>{index + 1}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{product.itemCode || product.prodId || '-'}</td>
                      <td>
                        <div style={{ fontSize: '13px' }}>{product.itemName || product.name || '-'}</div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{product.itemCode || product.prodId || ''}</div>
                      </td>
                      <td>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '4px', 
                          fontSize: '11px',
                          backgroundColor: getItemType(product) === 'Stock' ? '#e3f2fd' : '#fff3e0',
                          color: getItemType(product) === 'Stock' ? '#1976d2' : '#f57c00',
                          fontWeight: '500'
                        }}>
                          {getItemType(product) || '-'}
                        </span>
                      </td>
                      <td style={{ fontSize: '13px' }}>{product.uom || product.uomInfo?.uom?.uomName || '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: '500', fontSize: '13px' }}>
                        {existingStock.toFixed(2)}
                      </td>
                      <td>
                        <TextField
                          type="number"
                          size="small"
                          value={physicalQty}
                          onChange={(e) => updateQuantity(product._id, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onWheel={(e) => e.target.blur()}
                          placeholder="0.00"
                          inputProps={{ 
                            min: 0, 
                            step: 0.01,
                            style: { textAlign: 'right', fontSize: '13px' }
                          }}
                          sx={{
                            '& input[type=number]': { MozAppearance: 'textfield' },
                            '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                          }}
                          fullWidth
                        />
                      </td>
                      <td style={{ 
                        textAlign: 'right', 
                        fontWeight: '600',
                        fontSize: '13px',
                        color: variance > 0 ? '#4caf50' : variance < 0 ? '#f44336' : '#666'
                      }}>
                        {variance || '-'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    {!selectedBranch || !selectedLocation || !selectedDate 
                      ? 'Please select Date, Branch, and Location to load products'
                      : productsFromStockMoves.length === 0
                      ? 'No products found with stock movements for this date/location'
                      : searchTerm 
                      ? 'No products found matching your search'
                      : 'No products to display'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Snackbar */}
      {snackbar.open && (
        <div 
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '15px 20px',
            backgroundColor: snackbar.type === 'success' ? '#4caf50' : snackbar.type === 'error' ? '#f44336' : '#2196f3',
            color: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            zIndex: 9999
          }}
        >
          {snackbar.message}
          <button 
            onClick={() => setSnackbar({ ...snackbar, open: false })}
            style={{
              marginLeft: '15px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};
